import * as React from 'react';
import PropTypes from 'prop-types';
import { ConnectedConfiguratorScreen } from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/Screen';
import { DesignerScreen } from '@configuratorware/configurator-frontendgui/App/Modules/Designer/DesignerScreen';
import PdfDownload from '@configuratorware/configurator-frontendgui/App/Shared/Containers/PdfDownload';
import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { createPropsFromState } from '@configuratorware/configurator-frontendgui/App/Shared/Containers/Header';
import DesignerSelectionToolbox from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Containers/DesignerSelectionToolbox';
import DesignAreaControlbox from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Containers/DesignAreaControlbox';
import AmountPriceContainer from '../Containers/AmountPrice';
import CustomDesignerSelectionToolbox from '../Components/DesignerSelectionToolbox';
import CustomPdfDownload from '../Components/PdfDownload';
import CustomThemeProvider from '../ThemeProvider';
import CustomHeader from '../Components/CustomHeader';
import CustomDesignAreaControlbox from '../Components/DesignAreaControlbox';
import EditDesignButton from '../Components/EditDesignButton';
import DesignerClickAwayListener from '@configuratorware/configurator-frontendgui/App/Modules/Designer/MultilayerComponents/DesignerClickAwayListener';
import Designer from '../Containers/Designer';
import FallbackVisualization from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/Containers/FallbackVisualization';
import ImageControlboxComponent from '../Components/ImageControlboxComponent';

const headerTools = [
  {
    key: 'save',
    identifier: 'cloud_download',
    title: 'headerMenu.save',
    function: () => Services.interaction.saveConfiguration(),
    enabled: getConf('availableTools.save'),
  },
  {
    key: 'load',
    identifier: 'cloud_upload',
    title: 'headerMenu.load',
    function: () => {
      Services.interaction.showLoadConfigurationDialog();
    },
    enabled: getConf('availableTools.load'),
  },
  {
    key: 'mail',
    identifier: 'mail',
    title: 'headerMenu.sendEmail',
    function: async () => {
      await Services.interaction.mailConfiguration();
      Services.interaction.showSendEmailDialog();
    },
    enabled: getConf('availableTools.share'),
  },
  {
    key: 'archive',
    identifier: 'archive',
    title: 'headerMenu.saveAsPdf',
    function: () => Services.interaction.printConfigurationAndOpenPdf(),
    enabled: getConf('availableTools.print'),
  },
  {
    key: 'back',
    identifier: 'back',
    title: 'headerMenu.backToOverview',
    function: () => {},
    enabled: true,
  },
];

const renderCustomDesignerSelectionToolbox = (props) => (
  <React.Suspense fallback={<div>Loading Designer Toolbox...</div>}>
    <DesignerSelectionToolbox
      {...props}
      Component={CustomDesignerSelectionToolbox}
      ImageControlboxComponent={ImageControlboxComponent}
    />
  </React.Suspense>
);

const renderCustomEditDesignButton = (props) => (
  <EditDesignButton
    buttonXS={props.styledButton}
    openDesignArea={() => Services.designData.openDesignAreaEditor()}
  />
);

const renderCustomDesignAreaControlbox = ({ area }) => (
  <DesignAreaControlbox boxed={area === 'sidebar'} Component={CustomDesignAreaControlbox} />
);

const renderCustomAmountPrice = (props) => <AmountPriceContainer {...props} />; //Component={CustomAmountPrice} />

const renderCustomDesigner = (visualizationOverlayMode) => {
  const designer = <Designer visualizationOverlayMode={visualizationOverlayMode} />;
  return (
    <React.Suspense fallback={<div>Loading Designer Component...</div>}>
      {visualizationOverlayMode ? (
        <>
          <DesignerClickAwayListener />
          <div>{designer}</div>
        </>
      ) : (
        designer
      )}
    </React.Suspense>
  );
};

const CustomDesignerScreen = (props) => {
  const { visualizationOverlayMode, showDesigner, fallbackMode } = props;
  return (
    <DesignerScreen
      {...props}
      renderDesigner={() => (
        <FallbackVisualization fallbackMode={fallbackMode} showContent={showDesigner}>
          {showDesigner && renderCustomDesigner(visualizationOverlayMode)}
        </FallbackVisualization>
      )}
      renderDesignerSelectionToolbox={renderCustomDesignerSelectionToolbox}
      renderEditDesignButton={(props) => renderCustomEditDesignButton(props)}
      renderDesignAreaControlbox={(props) => renderCustomDesignAreaControlbox(props)}
      renderAmountPrice={(props) => renderCustomAmountPrice(props)}
      renderPdfDownload={(props) => <PdfDownload {...props} Component={CustomPdfDownload} />}
    />
  );
};

CustomDesignerScreen.propTypes = {
  visualizationOverlayMode: PropTypes.bool,
  showDesigner: PropTypes.bool,
  fallbackMode: PropTypes.bool,
};

const CustomConfiguratorScreen = (props) => (
  <ConnectedConfiguratorScreen
    {...props}
    ThemeProvider={CustomThemeProvider}
    renderHeader={() => (
      <CustomHeader
        tools={headerTools.filter((tool) => tool.enabled)}
        {...createPropsFromState(Services.store.state.configurator)}
        switchSelectedAmount={(oldIdentifier, newIdentifier) =>
          Services.configurator.switchSelectedAmount(oldIdentifier, newIdentifier)
        }
      />
    )}
    DesignerScreen={CustomDesignerScreen}
  />
);

export default CustomConfiguratorScreen;
